import React from 'react';
import { CASH_COUNT_BILLS, CASH_COUNT_COINS, computeCashCountTotal } from 'shared-models/browser';
import type { CashCountDenomination, SessionCashCountView } from 'shared-models/browser';
import { formatMoney, isolateAmountLtr } from '../../../../utils/money';
import { denominationKey, differenceLabel } from './cash-count.utils';
import { formatSlipDateTime } from '../../../../utils/slip-date';
import reportClasses from '../reports/pos-report-view.module.scss';
import classes from './cash-count-receipt.module.scss';

export type CashCountReceiptProps = {
  count: SessionCashCountView;
  header: string;
  isCopy: boolean;
  innerRef?: React.Ref<HTMLDivElement>;
};

const Money: React.FC<{ value: number }> = ({ value }) => <span>{isolateAmountLtr(formatMoney(value))} ₪</span>;

const Row: React.FC<{ label: string; value: React.ReactNode; bold?: boolean; dataTestId?: string }> = ({ label, value, bold = false, dataTestId }) => (
  <div className={bold ? `${reportClasses.row} ${reportClasses.rowBold}` : reportClasses.row} data-testid={dataTestId}>
    <span className={reportClasses.rowLabel}>{label}</span>
    <span className={reportClasses.rowValue}>{value}</span>
  </div>
);

const DenominationRows: React.FC<{ denominations: readonly CashCountDenomination[]; count: SessionCashCountView }> = ({ denominations, count }) => (
  <>
    {denominations.map((denomination) => {
      const quantity = count.quantities[denominationKey(denomination)] ?? 0;
      return (
        <React.Fragment key={denomination}>
          <span>{denomination} ₪</span>
          <span>{quantity}</span>
          <Money value={computeCashCountTotal({ [denominationKey(denomination)]: quantity })} />
        </React.Fragment>
      );
    })}
  </>
);

// Summary: Printable ספירת שטרות sheet rendered from the SERVER row so reprints match the original.
export const CashCountReceipt: React.FC<CashCountReceiptProps> = ({ count, header, isCopy, innerRef }) => (
  <div ref={innerRef} className={`${reportClasses.paper} ${reportClasses.paperPrint}`} data-testid="cash-count-receipt">
    <div className={reportClasses.header}>
      {header.split('\n').map((line, index) => (
        <span key={index} className={reportClasses.businessLine}>{line}</span>
      ))}
    </div>
    <div className={reportClasses.title}>ספירת שטרות</div>
    {isCopy && <div className={classes.copyBadge} data-testid="cash-count-receipt-copy-badge">עותק</div>}

    <div className={reportClasses.metaBlock}>
      <div className={reportClasses.metaRow}>משמרת: {count.sessionName}</div>
      <div className={reportClasses.metaRow}>תאריך: {formatSlipDateTime(count.updatedAt)}</div>
      {count.stationName && <div className={reportClasses.metaRow}>עמדה: {count.stationName}</div>}
      <div className={reportClasses.metaRow}>נספר ע"י: {count.countedByName ?? '--'}</div>
      {count.updatedByName && count.updatedByName !== count.countedByName && (
        <div className={reportClasses.metaRow}>עודכן ע"י: {count.updatedByName}</div>
      )}
    </div>

    <div className={`${reportClasses.section} ${classes.grid}`}>
      <span className={classes.gridHead}>ערך</span>
      <span className={classes.gridHead}>כמות</span>
      <span className={classes.gridHead}>סכום</span>
      <span className={classes.groupLabel}>שטרות</span>
      <DenominationRows denominations={CASH_COUNT_BILLS} count={count} />
      <span className={classes.groupLabel}>מטבעות</span>
      <DenominationRows denominations={CASH_COUNT_COINS} count={count} />
    </div>

    <div className={reportClasses.section}>
      <Row label='סה"כ נספר' value={<Money value={count.countedTotal} />} bold dataTestId="cash-count-receipt-total" />
      {count.expectedCash != null && <Row label="מזומן צפוי" value={<Money value={count.expectedCash} />} />}
      {count.difference != null && (
        <Row label={`הפרש (${differenceLabel(count.difference)})`} value={<Money value={count.difference} />} bold dataTestId="cash-count-receipt-difference" />
      )}
    </div>

    <div className={classes.signatures}>
      <div className={classes.signatureLine}>חתימת עובד: ____________</div>
      <div className={classes.signatureLine}>חתימת מנהל משמרת: ____________</div>
    </div>
  </div>
);
